/**
 * Source adapters. Everything the pipeline knows about where data comes from
 * lives behind these, so swapping to a keyed provider is a change here and
 * nowhere else.
 */

import type { TileKey } from '../geo/tilekey.js';

export type ElevationEncoding = 'terrarium' | 'terrain-rgb';

export interface ElevationSource {
  urlTemplate: string;
  encoding: ElevationEncoding;
  minZoom: number;
  maxZoom: number;
  /** Pixels per axis in a source tile. */
  tileSize: number;
  attribution: string;
  /**
   * Finer tiles over part of the world, tried first at their zoom, the
   * source's own where they have none: Lantmäteriet's laser ground model over
   * Sweden, say (`tools/lmdem.mjs`). Same encoding and tile size.
   */
  overlay?: { urlTemplate: string; zoom: number; attribution: string; index?: string };
}

export interface VectorSource {
  urlTemplate: string;
  minZoom: number;
  maxZoom: number;
  attribution: string;
  /**
   * Tiles of the same schema to take instead at `zoom`, where `index` lists
   * them: Sweden's own. `buildingsAttribution` credits the building
   * footprints their `lm_buildings` layer carries, shown on such a tile.
   */
  overlay?: { urlTemplate: string; zoom: number; index: string; buildingsAttribution?: string };
}

/** Expand a `{z}/{x}/{y}` template. */
export function expandTileUrl(template: string, key: TileKey): string {
  return template
    .replace('{z}', String(key.z))
    .replace('{x}', String(key.x))
    .replace('{y}', String(key.y));
}

/**
 * The tile to actually request for a desired tile.
 *
 * When the LOD asks for a zoom beyond what a source serves, fall back to the
 * deepest ancestor the source does have rather than requesting a 404. The
 * caller still meshes at the requested resolution, just from coarser data.
 */
export function clampToSourceZoom(key: TileKey, minZoom: number, maxZoom: number): TileKey {
  if (key.z <= maxZoom && key.z >= minZoom) return key;
  if (key.z > maxZoom) {
    const shift = key.z - maxZoom;
    return { z: maxZoom, x: key.x >> shift, y: key.y >> shift };
  }
  const shift = minZoom - key.z;
  return { z: minZoom, x: key.x << shift, y: key.y << shift };
}
